import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLocale } from '@/hooks/useLocale'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import Breadcrumb from '@/components/layout/Breadcrumb'
import SurahHeader from '@/components/quran/SurahHeader'
import SurahTabs, { type SurahTab } from '@/components/quran/SurahTabs'
import ReadingView from '@/components/quran/ReadingView'
import TranslationView from '@/components/quran/TranslationView'
import MushafView from '@/components/quran/MushafView'
import AudioPlayer from '@/components/quran/AudioPlayer'
import SurahNavigation from '@/components/quran/SurahNavigation'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { quranApi, type SurahDetail } from '@/services/quranApiService'
import { SUPPORTED_LANG_CODES } from '@/utils/localeFromUrl'
import {
  mergeQuranTranslationLanguageCodes,
  normalizeQuranLangCode,
} from '@/utils/quranTranslationLanguages'

export default function QuranSurahPage() {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { localizePath } = useLocale()

  // Default to translation tab if available, otherwise reading
  const [activeTab, setActiveTab] = useState<SurahTab>('translation')
  const [surah, setSurah] = useState<SurahDetail | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [highlightedAyah, setHighlightedAyah] = useState<number | undefined>()
  const [highlightedWord, setHighlightedWord] = useState<number | undefined>()
  
  // Translation language states - must be before any conditional returns
  const currentLang = i18n.language.split('-')[0] || 'en'
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(() =>
    mergeQuranTranslationLanguageCodes([...SUPPORTED_LANG_CODES]),
  )
  const [selectedTranslationLang, setSelectedTranslationLang] = useState<string>(() =>
    normalizeQuranLangCode(currentLang),
  )
  const [translationData, setTranslationData] = useState<{ [key: number]: string }>({})

  // Fetch surah data
  useEffect(() => {
    const fetchSurah = async () => {
      if (!id) {
        setError(t('quran.error.noSurahId', 'رقم السورة غير موجود'))
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        const surahNumber = parseInt(id, 10)
        if (Number.isNaN(surahNumber) || surahNumber < 1 || surahNumber > 114) {
          setError(t('quran.error.invalidSurahNumber', 'رقم السورة غير صحيح'))
          setIsLoading(false)
          return
        }

        // Get language from i18n - support all languages
        const lang = i18n.language.split('-')[0] || 'en'
        const data = await quranApi.getSurah(surahNumber, lang)

        setSurah(data)
        localStorage.setItem('lastReadSurahNumber', surahNumber.toString())
      } catch (err) {
        console.error('Failed to fetch surah:', err)
        setError(t('quran.error.loadSurahFailed', 'فشل تحميل بيانات السورة'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchSurah()
  }, [id, i18n.language, activeTab, t])

  // Fetch available translation languages (Quran.com v4)
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const langs = await quranApi.getTranslationLanguages()
        setAvailableLanguages(mergeQuranTranslationLanguageCodes(langs))
      } catch (err) {
        console.error('Failed to fetch translation languages:', err)
      }
    }
    fetchLanguages()
  }, [])

  // Keep selected translation code in sync with merged list (e.g. API sent "english" → "en")
  useEffect(() => {
    if (availableLanguages.length === 0) return
    setSelectedTranslationLang((prev) => {
      const prevN = normalizeQuranLangCode(prev)
      if (availableLanguages.includes(prevN)) return prevN
      const ui = normalizeQuranLangCode(i18n.language?.split('-')[0] || 'en')
      if (availableLanguages.includes(ui)) return ui
      if (availableLanguages.includes('en')) return 'en'
      return availableLanguages[0]
    })
  }, [availableLanguages, i18n.language])

  // Fetch translation when language changes
  useEffect(() => {
    const fetchTranslation = async () => {
      if (!id || !surah) return
      
      if (selectedTranslationLang === 'ar') {
        // Arabic doesn't need translation - use Arabic text
        const translations: { [key: number]: string } = {}
        const ayahsSource = getAyahsSource(surah)
        ayahsSource.forEach((ayah) => {
          translations[ayah.number] = ayah.arabicText
        })
        setTranslationData(translations)
        return
      }

      try {
        const surahNumber = parseInt(id, 10)
        if (Number.isNaN(surahNumber)) return

        const data = await quranApi.getSurah(surahNumber, selectedTranslationLang)
        
        // Extract translations from ayahsList
        const translations: { [key: number]: string } = {}
        if (data.ayahsList && Array.isArray(data.ayahsList)) {
          data.ayahsList.forEach((ayah: any) => {
            // Try multiple possible properties for translation
            let translationText = ayah.translation || 
                                 ayah.translations?.[selectedTranslationLang] ||
                                 (ayah.translations && typeof ayah.translations === 'object' ? Object.values(ayah.translations)[0] : null) ||
                                 null
            
            if (translationText && String(translationText).trim()) {
              translations[ayah.number] = String(translationText).trim()
            }
          })
        }
        setTranslationData(translations)
      } catch (err) {
        console.error('Failed to fetch translation:', err)
        // Keep existing translations or use empty
        setTranslationData({})
      }
    }

    if (surah) {
      fetchTranslation()
    }
  }, [id, selectedTranslationLang, surah])

  // Helper function to get ayahs source
  const getAyahsSource = (surahData: SurahDetail) => {
    if (!surahData) {
      console.warn('⚠️ surahData is null or undefined')
      return []
    }
    
    const anySurah: any = surahData
    
    // Try ayahsList first (standard property)
    if (surahData.ayahsList && Array.isArray(surahData.ayahsList) && surahData.ayahsList.length > 0) {
      return surahData.ayahsList as { number: number; arabicText: string; translation?: string | null }[]
    }
    
    // Fallback to ayahs (alternative property name)
    if (anySurah.ayahs && Array.isArray(anySurah.ayahs) && anySurah.ayahs.length > 0) {
      console.log('📋 Using ayahs property instead of ayahsList')
      return anySurah.ayahs as { number: number; arabicText: string; translation?: string | null }[]
    }
    
    // No ayahs found
    console.warn('⚠️ No ayahs found in surahData:', {
      hasAyahsList: !!surahData.ayahsList,
      ayahsListLength: surahData.ayahsList?.length || 0,
      hasAyahs: !!anySurah.ayahs,
      ayahsLength: anySurah.ayahs?.length || 0,
      surahDataKeys: Object.keys(surahData)
    })
    return []
  }

  const handleWordHighlight = (ayahNumber: number, wordIndex: number) => {
    setHighlightedAyah(ayahNumber)
    setHighlightedWord(wordIndex)

    const ayahElement = document.getElementById(`ayah-${ayahNumber}`)
    if (ayahElement) {
      ayahElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleAyahChange = (ayahNumber: number) => {
    setHighlightedAyah(ayahNumber)
    setHighlightedWord(undefined)
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (error || !surah) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-gray-600">{error || t('public.quran.notFound')}</p>
        <button
          onClick={() => navigate(localizePath('/quran'))}
          className="mt-4 text-primary-600 hover:underline"
        >
          {t('public.quran.backToIndex')}
        </button>
      </div>
    )
  }

  // Get ayahs source
  const ayahsSource = getAyahsSource(surah)

  // لو مفيش آيات خالص
  if (!ayahsSource.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb
          items={[
            { label: t('common.home'), to: '/' },
            { label: t('common.quran'), to: '/quran' },
            { label: surah.name },
          ]}
          className="mb-6"
        />
        <SurahHeader surah={surah} />
        <p className="mt-6 text-center text-gray-600">
          لم يتم العثور على آيات لهذه السورة في الاستجابة الراجعة من الـ API.
        </p>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.quran'), to: '/quran' },
    { label: surah.name },
  ]

  // بيانات القراءة
  const readingAyahs = ayahsSource
    .filter((ayah) => ayah && ayah.arabicText && ayah.arabicText.trim())
    .map((ayah) => ({
      number: ayah.number,
      text: ayah.arabicText,
    }))

  // بيانات الترجمة - دعم جميع اللغات
  const translationAyahs = ayahsSource.map((ayah) => {
    // Get translation from fetched data - try multiple sources
    let translation = translationData[ayah.number] || ''
    
    // Fallback: try to get from original ayah if available
    if (!translation && ayah.translation) {
      translation = ayah.translation
    }
    
    // Build translations object
    const translations: { [key: string]: string } = {
      'ar': ayah.arabicText, // Arabic text is always available
    }
    
    // Add selected language translation
    if (translation && translation.trim()) {
      translations[selectedTranslationLang] = String(translation).trim()
    }
    
    return {
      number: ayah.number,
      arabicText: ayah.arabicText,
      translations,
    }
  })

  // Navigation
  const previousSurah =
    surah.number > 1
      ? { id: surah.number - 1, name: t('public.quran.prevSurah') }
      : null
  const nextSurah =
    surah.number < 114
      ? { id: surah.number + 1, name: t('public.quran.nextSurah') }
      : null

  return (
    <>
      <Helmet>
        <title>
          {surah.name} - {t('common.quran')} - سكينة
        </title>
        <meta
          name="description"
          content={t('public.quran.readSurah', { name: surah.name })}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <SurahHeader surah={surah} />

        <SurahTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === 'reading' && (
          <ReadingView
            ayahs={readingAyahs}
            highlightedAyah={highlightedAyah}
            highlightedWord={highlightedWord}
          />
        )}

        {activeTab === 'translation' && (
          <TranslationView
            ayahs={translationAyahs}
            availableLanguages={availableLanguages}
            defaultLanguage={selectedTranslationLang}
            onLanguageChange={setSelectedTranslationLang}
          />
        )}

        {/* {activeTab === 'mushaf' && (
          <MushafView
            surahNumber={surah.number}
            initialPage={readingAyahs.length > 0 ? readingAyahs[0].number : 1}
          />
        )} */}

        {activeTab === 'audio' && (
          <AudioPlayer
            surahNumber={surah.number}
            ayahs={readingAyahs}
            onWordHighlight={handleWordHighlight}
            onAyahChange={handleAyahChange}
          />
        )}

        <SurahNavigation previousSurah={previousSurah} nextSurah={nextSurah} />
      </div>
    </>
  )
}
